import clone from 'clone';
import { Normalizers } from './Normalizers';
import { Validators } from './Validators';
import ASSET_TYPES from '../types/AssetTypes';
import { SEQUENCE } from '../types/SequenceType';
import { DEFAULT_PLAYLIST_REPEAT_VALUE, PLAYLIST_REPEAT_VALUES } from '../types/PlaylistValues';
import AssignmentSelectionHelper from '../utils/AssignmentSelectionHelper';

/**
 * @typedef {import('../types/ContentItems').ContentItemsResponse} ContentItemsResponse
 * @typedef {import('../types/ContentItems').ContentItem} ContentItem
 * @typedef {import('../types/User').User} User
 * @typedef {import('../types/Assignments').Assignment} Assignment
 * @typedef {import('../types/Assignments').Assignments} Assignments
 * @typedef {import('../types/CampaignSchedule').CampaignScheduleEntry} CampaignScheduleEntry
 * @typedef {import('../types/Playlist').PlaylistResponse} Playlist
 * @typedef {import('../types/Resolution').Resolution} Resolution
 * @typedef {import('../types/Sequences').SequencesResponse} SequencesResponse
 * @typedef {import('../types/Sequences').Sequence} Sequence
 * @typedef {import('../types/Sequences').SequenceEntry} SequenceEntry
 * @typedef {import('./ValidateResult').ValidateResult} ValidateResult
 * @typedef {import('../types/ScheduleValue').ScheduleValue} ScheduleValue
 *
 * Manages the state data for the Multi-Media Wizard.
 *
 * API Data is prefixed with "api_" to distinguish from custom data.
 */
export default class WizardData {
  /**
   * React state data
   * Current state. Read-only; do not mutate directly
   * @type {Object}
   */
  #Reactdata;

  /**
   * Updates the state. Can take a value or updater function. Causes re-render.
   * use this.#setData
   * @type {Function}
   * @see this.#setData
   */
  #ReactSetData;

  /**
   * Helper class for managing assignment selection logic.
   * @type {AssignmentSelectionHelper}
   */
  #assignmentSelectionHelper;

  /**
   * Constructor
   * @param {Object} data - The current state data.
   * @param {Function} setData - The state update function.
   */
  constructor(data, setData) {
    this.#Reactdata = data;
    this.#ReactSetData = setData;
    this.#assignmentSelectionHelper = new AssignmentSelectionHelper(
      this.api_allAssignments,
      this.selectedAssignments,
      this.editSequenceId,
      this.schedule,
    );
  }

  /**
   * Value Setter
   * Sets a value in the data object and triggers a re-render.
   * @param {string} id - The key of the data to set.
   * @param {any} value - The value to set.
   */
  #setData(id, value) {
    this.#ReactSetData(prev => ({
      ...prev,
      [id]: value,
    }));
  }

  // Navigation ---------------------------------------------------------------

  /**
   * Gets the next step index for navigation.
   * @returns {number} The next step index.
   */
  get navigationNextIndex() {
    return this.#Reactdata.navigationIndexes.nextIndex;
  }

  /**
   * Gets the previous step index for navigation.
   * @returns {number} The previous step index.
   */
  get navigationPrevIndex() {
    return this.#Reactdata.navigationIndexes.prevIndex;
  }

  /**
   * Sets the navigation indexes.
   * @param {number} prevIndex - The previous step index.
   * @param {number} nextIndex - The next step index.
   */
  navigationSetIndexes(prevIndex, nextIndex) {
    this.#setData('navigationIndexes', { prevIndex, nextIndex });
  }

  /**
   * Sets the current step index.
   * @param {number} stepIndex - The step index to set.
   */
  navigationSetStep(stepIndex) {
    this.#setData('stepIndex', stepIndex);
  }

  /**
   * Advances to the next step in the wizard.
   */
  navigationNextStep() {
    this.navigationSetStep(this.navigationNextIndex);
  }

  /**
   * Advances to the previous step in the wizard.
   */
  navigationPrevStep() {
    this.navigationSetStep(this.navigationPrevIndex);
  }

  // External API Data --------------------------------------------------------
  // should be set from API calls via Loader.js -------------------------------

  /**
   * Data from API: Assets
   * @type {ContentItemsResponse}
   */
  get api_assets() {
    return this.#Reactdata.api_assets;
  }

  /**
   * @param {ContentItemsResponse} value
   */
  set api_assets(value) {
    this.#markResolutionMismatchInAssets(value.content_items);
    this.#setData('api_assets', value);
  }

  /**
   * Data from API: Users
   * @type {User[]}
   */
  get api_users() {
    return this.#Reactdata.api_users || [];
  }

  /**
   * @param {User[]} value - The new users array.
   */
  set api_users(value) {
    this.#setData('api_users', value);
  }

  /**
   * Data from API: current User
   * @type {User}
   */
  get api_user() {
    return this.#Reactdata.api_user;
  }

  /**
   * @param {User} value - Current User Object
   */
  set api_user(value) {
    this.#setData('api_user', value);
  }

  /**
   * Data from API: Campaign Schedules
   * @type {CampaignScheduleEntry[]}
   */
  get api_campaignSchedules() {
    return this.#Reactdata.api_campaignSchedules || [];
  }

  /**
   * @param {CampaignScheduleEntry[]} value - The new campaign schedules array.
   */
  set api_campaignSchedules(value) {
    this.#setData('api_campaignSchedules', value);
  }

  /**
   * Data from API: Assignments filtered by search
   * to include custom data ( contentCount, deviceCount) for the AssignmentTable
   * we need to decorate the assignments, but we cannot hook on api_allAssignments setter
   * so we just map to all assignments here to find the matching ones, they are decorated there already
   * @type {Assignments}
   */
  get api_filteredAssignments() {
    if (!this.#Reactdata.api_filteredAssignments) {
      return undefined;
    }

    const assignments = this.#Reactdata.api_filteredAssignments.assignments.map(assignment => {
      return (
        this.api_allAssignments?.assignments.find(
          a => a.id === assignment.id && a.type === assignment.type,
        ) || assignment
      );
    });

    this.#Reactdata.api_filteredAssignments.assignments = assignments;

    return this.#Reactdata.api_filteredAssignments;
  }

  /**
   * @param {Assignments} value Assignments filtered by search
   */
  set api_filteredAssignments(value) {
    this.#setData('api_filteredAssignments', value);
  }

  /**
   * Data from API: All possible Assignments
   * @type {Assignments}
   */
  get api_allAssignments() {
    return this.#Reactdata.api_allAssignments;
  }

  /**
   * Sets the all assignments data and initializes the assignment selection helper with it.
   * If the all assignments data is being set for the first time,
   * it also pre-selects any device assignments that are part of the sequence being edited.
   * @param {Assignments} value All possible Assignments
   */
  set api_allAssignments(value) {
    if (!this.#Reactdata.api_allAssignments) {
      const helper = new AssignmentSelectionHelper(value, [], this.editSequenceId, this.schedule);

      value.assignments = value.assignments.map(assignment => {
        return helper.decorateAssignment(assignment);
      });

      value.assignments
        .filter(
          assignment =>
            assignment.content_type === SEQUENCE &&
            Number(assignment.content_id) === this.editSequenceId,
        )
        .forEach(assignment => {
          helper.toggleAssignment(assignment);
        });

      this.#ReactSetData(prev => ({
        ...prev,
        api_allAssignments: value,
        selectedAssignments: helper.selectedAssignments,
      }));

      return;
    }

    // itt triggerelemt a reast set state, re-renderel is
    this.#setData('api_allAssignments', value);
  }

  /**
   * Data from API: All Sequences
   * @type {SequencesResponse}
   */
  get api_allSequences() {
    return this.#Reactdata.api_allSequences;
  }

  /**
   * @param {SequencesResponse} value All Sequences
   */
  set api_allSequences(value) {
    this.#setData('api_allSequences', value);
  }

  // Sequence Data manipulation -----------------------------------------------

  /**
   * Gets the edit sequence ID from React data.
   * @returns {number|null} The edit sequence ID or null if not set.
   */
  get editSequenceId() {
    return this.#Reactdata.editSequenceId || null;
  }

  /**
   * @param {number|null} id - The edit sequence ID to set.
   */
  set editSequenceId(id) {
    this.#setData('editSequenceId', id);
  }

  /**
   * Local cache of sequence data
   * React data is read-only, so we cache it here for easier manipulation
   * should be accessed via the sequence getter
   * @type {Sequence}
   */
  #localSequence;

  /**
   * Gets the sequence data.
   * @returns {Sequence} The current sequence data.
   */
  get sequence() {
    if (!this.#localSequence) {
      this.#localSequence = this.#Reactdata.sequence || {
        id: undefined,
        name: undefined,
        description: undefined,
        createdBy: undefined,
        createdDate: undefined,
        modifiedBy: undefined,
        modifiedDate: undefined,
        parent: undefined,
        tags: [],
        sequenceEntries: [],
        // custom fields
        assets: [],
        targetResolution: undefined,
      };
    }

    return this.#localSequence;
  }

  /**
   * Sets the sequence data.
   * Warning, this should only happen if we are editing an existing sequence from API
   * @param {Sequence} value - The new sequence data.
   */
  set sequence(value) {
    this.#localSequence = value;
    this.#sequenceUpdated();
  }

  /**
   * Saves the sequence data to be used internally after changes to sequence properties
   * this will trigger re-render
   */
  #sequenceUpdated() {
    this.#markResolutionMismatchInAssets(this.selectedAssets);
    this.#regenerateSequenceEntriesFromAssets();
    this.#setData('sequence', { ...this.sequence });
  }

  // Sequence Entries ---------------------------------------------------------

  /**
   * Gets the array of sequence entries.
   * @returns {SequenceEntry[]} The sequence entries.
   */
  get sequenceEntries() {
    return this.sequence.sequenceEntries || [];
  }

  /**
   * Sets the array of sequence entries.
   * @param {SequenceEntry[]} value - The new sequence entries.
   */
  set sequenceEntries(value) {
    this.sequence.sequenceEntries = value;
    this.#sequenceUpdated();
  }

  /**
   * Creates a sequence entry object for the given asset.
   *
   * exclusively for #regenerateSequenceEntriesFromAssets
   * @param {ContentItem} asset - The asset to create the sequence entry for.
   * @param {SequenceEntry} overrides - Optional overrides for the sequence entry properties.
   * @returns {SequenceEntry} The created sequence entry.
   */
  #makeSequenceEntry(asset, overrides = {}) {
    return {
      id: null,
      sequence_id: null,
      sign_id: overrides?.sign_id,
      playlist_id: null,
      channel_id: null,
      order: null,
      repeat: overrides?.repeat || asset?.repeat || PLAYLIST_REPEAT_VALUES.dont_loop,
      asset,
    };
  }

  /**
   * Creates a playlist entry object.
   *
   * exclusively for #regenerateSequenceEntriesFromAssets
   * @returns {Playlist} The created playlist entry.
   */
  #makePlaylistEntry() {
    return {
      id: null,
      content_id: null,
      name: 'playlist',
      description: null,
      createdBy: null,
      createdDate: null,
      modifiedBy: null,
      modifiedDate: null,
      tagControlled: null,
      parent: null,
      usedInSigns: [],
      tags: [],
      files: [],
      playlistEntries: [],
      // custom fields
      type: ASSET_TYPES.playlist,
      duration: 0,
      repeat: DEFAULT_PLAYLIST_REPEAT_VALUE,
    };
  }

  /**
   * Adds a video asset to the last playlist in the sequence entries,
   * or creates a new playlist if the last entry is not a playlist.
   *
   * exclusively for #regenerateSequenceEntriesFromAssets
   * @param {Playlist} videoAsset - The video asset to add.
   */
  #addVideoToPlaylist(videoAsset) {
    const lastEntry = this.sequence.sequenceEntries.at(-1);
    let playlistAsset = lastEntry?.asset;

    // either, not a playlist, or has id, saved hence read-only
    if (playlistAsset?.type !== ASSET_TYPES.playlist || playlistAsset?.id) {
      playlistAsset = this.#makePlaylistEntry();

      this.sequence.sequenceEntries.push(this.#makeSequenceEntry(playlistAsset));
    }

    playlistAsset.id = this.#stripPrefixedId(playlistAsset.id);
    playlistAsset.width = videoAsset.width;
    playlistAsset.height = videoAsset.height;
    playlistAsset.duration += videoAsset.duration;
    playlistAsset.files.push(videoAsset);
    playlistAsset.playlistEntries.push(videoAsset);
  }

  /**
   * Strips any prefix from the given ID string.
   * This is useful for converting IDs that may have prefixes (e.g., "asset-123") to a pure numeric ID ("123").
   * @param {string|number|null|undefined} id - The ID string to strip the prefix from.
   * @returns {number|null} The numeric ID with the prefix removed.
   */
  #stripPrefixedId(id) {
    if (!id) {
      return null;
    }

    return Number(id.toString().replace(/^[a-zA-Z]+-/, ''));
  }

  /**
   * Regenerates the sequence entries based on the selected assets.
   * This method clears the existing sequence entries and creates new ones
   * based on the types of the selected assets.
   * exclusively for #sequenceUpdated
   */
  #regenerateSequenceEntriesFromAssets() {
    this.sequence.sequenceEntries = [];

    this.selectedAssets.forEach(asset => {
      switch (asset.type) {
        case ASSET_TYPES.image:
          this.sequence.sequenceEntries.push(this.#makeSequenceEntry(asset));
          break;
        case ASSET_TYPES.video:
          this.#addVideoToPlaylist(asset);
          break;
        case ASSET_TYPES.sign:
          this.sequence.sequenceEntries.push(this.#makeSequenceEntry(asset, { sign_id: asset.id }));
          break;
        case ASSET_TYPES.playlist:
          this.sequence.sequenceEntries.push(
            this.#makeSequenceEntry(asset, {
              repeat: asset.repeat || DEFAULT_PLAYLIST_REPEAT_VALUE,
            }),
          );
          break;
        default:
          break;
      }

      const indexId = this.sequence.sequenceEntries.length;
      const entry = this.sequence.sequenceEntries[indexId - 1];

      entry.order = indexId;
      // deprecated, but set to -1 to avoid confusion with actual channel assignment
      entry.channel_id = -1;
      // revert sign-123 to 123 for internal use, as sequence entry sign_id should be pure numeric
      entry.sign_id = this.#stripPrefixedId(entry.sign_id);
      entry.playlist_id = asset.playlist_id || -1;
    });
  }

  // Selected Assignments -----------------------------------------------------

  /**
   * Gets the selected assignments map.
   * @returns {Object<string, Assignment>} The selected assignments map.
   */
  get selectedAssignments() {
    return this.#Reactdata.selectedAssignments || {};
  }

  /**
   * Gets the Set of selected assignment IDs.
   * @returns {string[]} selected IDs.
   */
  get selectedDeviceIds() {
    return this.#assignmentSelectionHelper.selectedDeviceIds;
  }

  /**
   * Gets the Set of selected device assignments.
   * @returns {Assignment[]} selected device assignments.
   */
  get selectedDeviceAssignments() {
    return this.#assignmentSelectionHelper.selectedDeviceAssignments;
  }

  /**
   * Gets the Set of selected assignment location IDs.
   * @returns {string[]} selected location IDs.
   */
  get selectedLocationIds() {
    return this.#assignmentSelectionHelper.selectedLocationIds;
  }

  /**
   * Checks if an assignment is selected.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if selected, false otherwise.
   */
  isAssignmentSelected(assignment) {
    return this.#assignmentSelectionHelper.isAssignmentSelected(assignment);
  }

  /**
   * Checks if an assignment is already assigned to the sequence being edited.
   * This effectively renders the assignment read-only in the UI, as it cannot be unselected.
   * @param {Assignment} assignment - The assignment to check.
   * @returns {boolean} True if already assigned, false otherwise.
   */
  isAssignmentAlreadyAssigned(assignment) {
    return this.#assignmentSelectionHelper.isAssignmentAlreadyAssigned(assignment);
  }

  /**
   * Toggles the selection state of an assignment.
   * @param {Assignment} assignment - The assignment to toggle.
   */
  toggleAssignment(assignment) {
    this.#assignmentSelectionHelper.toggleAssignment(assignment);
    this.#setData('selectedAssignments', this.#assignmentSelectionHelper.selectedAssignments);
  }

  /**
   * Resets the selected assignments to an empty state, effectively unselecting all assignments.
   * This can be used, for example, when changing the schedule to clear any assignments that may no longer be relevant.
   */
  resetSelectedAssignments() {
    this.#setData('selectedAssignments', {});
  }

  // Sequence Name ------------------------------------------------------------

  /**
   * The minimum allowed length for the name.
   * @type {number}
   * @constant
   */
  static NAME_MIN_LENGTH = 3;

  /**
   * The maximum allowed length for the name.
   * @type {number}
   * @constant
   */
  static NAME_MAX_LENGTH = 32;

  /**
   * Gets the name value.
   * @returns {string} The current name.
   */
  get name() {
    return this.sequence.name || '';
  }

  /**
   * Sets the name value after normalizing it.
   * @param {string} value - The new name value to set.
   */
  set name(value) {
    this.sequence.name = Normalizers.trimString(value);
    this.#sequenceUpdated();
  }

  /**
   * Validates the current name value.
   * @returns {ValidateResult} The result of the name validation.
   */
  isNameValid() {
    const lengthValid = Validators.stringBetweenLengths(
      'Name',
      this.name,
      WizardData.NAME_MIN_LENGTH,
      WizardData.NAME_MAX_LENGTH,
    );

    if (!lengthValid.isValid) {
      return lengthValid;
    }

    if (this.editSequenceId === this.sequence.id) {
      // if we are editing an existing sequence
      const currentSequence = this.api_allSequences?.sequences.find(
        seq => seq.id === this.sequence.id,
      );

      if (!currentSequence) {
        return Validators.isNotEmpty('Name'); // should not happen, but if sequence not found, validate as new name
      }

      if (this.name === currentSequence.name) {
        return Validators.successValidate('Name');
      }
    }

    return Validators.isUnique(
      'Name',
      this.name,
      this.api_allSequences?.sequences.map(seq => seq.name) || [],
      true,
    );
  }

  // Sequence targeted Resolution ---------------------------------------------

  /**
   * The maximum allowed resolution value.
   * @type {number}
   * @constant
   */
  static RESOLUTION_MAX = 50000;

  /**
   * Gets the resolution value.
   * @returns {Resolution} The current resolution.
   */
  get resolution() {
    return this.sequence.targetResolution || { x: 1920, y: 1080 };
  }

  /**
   * Sets the resolution value after normalizing its x and y components.
   * @param {Resolution} value - The new resolution value to set.
   */
  set resolution(value) {
    this.sequence.targetResolution = {
      x: Normalizers.parseNumber(value?.x),
      y: Normalizers.parseNumber(value?.y),
    };
    this.#sequenceUpdated();
  }

  /**
   * Validates the current X resolution value.
   * @returns {ValidateResult} The result of the resolution validation.
   */
  isResolutionXValid() {
    const xTypeInt = Validators.isInteger('Resolution X', this.resolution.x);

    if (!xTypeInt.isValid) {
      return xTypeInt;
    }

    const xValid = Validators.numberInRange(
      'Resolution X',
      this.resolution.x,
      1,
      WizardData.RESOLUTION_MAX,
    );

    if (xValid.isValid === false) {
      return xValid;
    }

    return Validators.successValidate('Resolution');
  }

  /**
   * Validates the current Y resolution value.
   * @returns {ValidateResult} The result of the resolution validation.
   */
  isResolutionYValid() {
    const yTypeInt = Validators.isInteger('Resolution Y', this.resolution.y);

    if (!yTypeInt.isValid) {
      return yTypeInt;
    }

    const yValid = Validators.numberInRange(
      'Resolution Y',
      this.resolution.y,
      1,
      WizardData.RESOLUTION_MAX,
    );

    if (yValid.isValid === false) {
      return yValid;
    }

    return Validators.successValidate('Resolution');
  }

  /**
   * Validates the current resolution value.
   * @returns {ValidateResult} The result of the resolution validation.
   */
  isResolutionValid() {
    const props = Validators.propertiesExist('Resolution', this.resolution, ['x', 'y']);

    if (!props.isValid) {
      return props;
    }

    const xValid = this.isResolutionXValid();

    if (!xValid.isValid) {
      return xValid;
    }

    const yValid = this.isResolutionYValid();

    if (!yValid.isValid) {
      return yValid;
    }

    return Validators.successValidate('Resolution');
  }

  /**
   * Marks assets with resolution mismatch.
   * exclusively for #sequenceUpdated
   * @param {ContentItem[]} assets - The array of asset objects to check for resolution mismatch.
   */
  #markResolutionMismatchInAssets(assets) {
    assets.forEach(asset => {
      asset.resolutionMismatch =
        asset?.width !== this.resolution.x || asset?.height !== this.resolution.y;
    });
  }

  // targeted Campaign Schedule -----------------------------------------------

  /**
   * Gets the active campaign schedule ID.
   * @returns {number|null} The active campaign schedule ID or null if not set.
   */
  get activeCampaignId() {
    return this.#Reactdata.activeCampaignId || null;
  }

  /**
   * Sets the active campaign schedule ID.
   * @param {number|null} value - The active campaign schedule ID to set.
   */
  set activeCampaignId(value) {
    this.#setData('activeCampaignId', value);
  }

  /**
   * Gets the target campaign
   * @returns {CampaignScheduleEntry|null} The target campaign schedule or null if not set.
   */
  get targetCampaign() {
    return this.#Reactdata.targetCampaign || null;
  }

  /**
   * Sets the target campaign
   * @param {CampaignScheduleEntry|null} value
   */
  set targetCampaign(value) {
    this.#setData('targetCampaign', value);
  }

  /**
   * Validates the target campaign
   * @returns {ValidateResult} The result of the target campaign validation.
   */
  isTargetCampaignValid() {
    return Validators.validateCampaignSchedule(this.targetCampaign);
  }

  // Selected Sequence.Assets -------------------------------------------------

  /**
   * Gets the array of selected asset objects.
   * @returns {ContentItem[]} The selected assets.
   */
  get selectedAssets() {
    return this.sequence.assets || [];
  }

  /**
   * Add an asset to the selection
   * @param {ContentItem} asset - Asset object to add
   * @param {number} defaultDurationMs - Default duration in milliseconds for assets without a duration.
   * @returns {ValidateResult} The result of the validation.
   */
  addAsset(asset, defaultDurationMs) {
    asset = clone(asset);
    asset.duration = asset.duration > 0 ? asset.duration : defaultDurationMs;

    const lastAddedAsset = this.getLastAddedAsset();
    const validateResult = Validators.validateAsset(
      asset,
      lastAddedAsset?.id,
      lastAddedAsset?.type,
    );

    if (validateResult.isValid) {
      this.sequence.assets.push(asset);
      this.#sequenceUpdated();
    }

    return validateResult;
  }

  /**
   * Gets the last added asset.
   * @returns {ContentItem|null} The last added asset or null if none exist.
   */
  getLastAddedAsset() {
    if (this.selectedAssets.length === 0) {
      return null;
    }

    return this.selectedAssets.at(-1);
  }

  /**
   * Update the duration of an asset at a specific index.
   * @param {number} index - Index of the asset to update.
   * @param {number} duration - (ONLY image type) New duration in seconds.
   */
  updateAssetDuration(index, duration) {
    if (index >= 0 && index < this.selectedAssets.length) {
      const asset = this.selectedAssets[index];

      if (asset.type === ASSET_TYPES.image) {
        asset.duration = Number(duration) * 1000;
      }

      this.#sequenceUpdated();
    }
  }

  /**
   * Update an asset at a specific index with new asset data.
   * @param {number} index - Index of the asset to update.
   * @param {ContentItem} updatedAsset - The updated asset object.
   * @see this.sequence.assets
   */
  updateAssetAt(index, updatedAsset) {
    if (index >= 0 && index < this.selectedAssets.length) {
      this.sequence.assets[index] = updatedAsset;
      this.#sequenceUpdated();
    }
  }

  /**
   * Move an asset from one index to another.
   * @param {number} fromIndex - Index of the item to move.
   * @param {number} toIndex - Index to move the item to.
   */
  moveAsset(fromIndex, toIndex) {
    if (
      fromIndex < 0 ||
      fromIndex >= this.selectedAssets.length ||
      toIndex < 0 ||
      toIndex >= this.selectedAssets.length
    ) {
      return;
    }

    const assets = [...this.selectedAssets];
    const [item] = assets.splice(fromIndex, 1);

    assets.splice(toIndex, 0, item);

    this.sequence.assets = assets;
    this.#sequenceUpdated();
  }

  /**
   * Remove an asset at a specific index
   * @param {number} index - Index of the asset to remove
   */
  removeAssetAt(index) {
    if (index >= 0 && index < this.selectedAssets.length) {
      this.sequence.assets.splice(index, 1);
      this.#sequenceUpdated();
    }
  }

  /**
   * Validates the selected assets.
   * @returns {ValidateResult} The result of the validation.
   */
  isAssetSelectionValid() {
    const notEmpty = Validators.isNotEmpty('Selected Assets', this.selectedAssets);

    if (!notEmpty.isValid) {
      return notEmpty;
    }

    let invalidResult;

    this.selectedAssets.some(asset => {
      const result = Validators.validateAsset(asset);

      if (!result.isValid) {
        invalidResult = result;

        return true; // stop iterating
      }

      return false;
    });

    if (invalidResult) {
      return invalidResult;
    }

    return Validators.successValidate('Selected Assets');
  }

  // Schedule -----------------------------------------------------------------

  /**
   * Gets the schedule value.
   * @returns {ScheduleValue|null} The current schedule value or null if not set.
   */
  get schedule() {
    return this.#Reactdata.schedule || null;
  }

  /**
   * Sets the schedule value.
   * @param {ScheduleValue|null} value - The new schedule value to set.
   */
  set schedule(value) {
    this.#setData('schedule', value);
  }

  /**
   * Validates the selected schedule option.
   * @returns {ValidateResult} The result of the validation.
   */
  isScheduleValid() {
    const schedule = this.schedule;

    return Validators.validateSchedule(schedule);
  }
}
